// /home/Hakyury/sites/oauth.eight-percent.com/frontend/src/utils/authFetch.js

let refreshPromise = null;
let refreshTimeoutId = null;

export const REFRESH_ENDPOINT = 'https://oauth.eight-percent.com/api/accounts/token/refresh/';
const SKEW_SECONDS = 45;

// Ключи для хранения токенов
const TOKEN_KEYS = {
  ACCESS: 'eight_percent_access_token',
  REFRESH: 'eight_percent_refresh_token'
};

// Синхронизированное хранение токенов
export function getTokens() {
  return {
    access: localStorage.getItem(TOKEN_KEYS.ACCESS),
    refresh: localStorage.getItem(TOKEN_KEYS.REFRESH)
  };
}

export function setTokens({ access, refresh }) {
  if (access) {
    localStorage.setItem(TOKEN_KEYS.ACCESS, access);
  }
  
  if (refresh) {
    localStorage.setItem(TOKEN_KEYS.REFRESH, refresh);
  }
  
  // Синхронизируем между вкладками
  window.dispatchEvent(new Event('storage'));
  scheduleProactiveRefresh();
}

export function clearTokens() {
  localStorage.removeItem(TOKEN_KEYS.ACCESS);
  localStorage.removeItem(TOKEN_KEYS.REFRESH);
  clearTimeout(refreshTimeoutId);
  
  // Синхронизируем между вкладками
  window.dispatchEvent(new Event('storage'));
}

function decodeJwt(token) {
  try {
    const [, payload] = token.split('.');
    return JSON.parse(
      atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
    );
  } catch {
    return null;
  }
}

function getExp(token) {
  const p = decodeJwt(token);
  return p?.exp || 0;
}

function isExpiring(token, skew = SKEW_SECONDS) {
  if (!token) return true;
  const exp = getExp(token);
  if (!exp) return true;
  const now = Math.floor(Date.now() / 1000);
  return exp - now <= skew;
}

function scheduleProactiveRefresh(refreshEndpoint = REFRESH_ENDPOINT) {
  clearTimeout(refreshTimeoutId);
  const { access } = getTokens();
  const exp = getExp(access);
  if (!exp) return;
  const nowMs = Date.now();
  const delayMs = Math.max(exp * 1000 - nowMs - SKEW_SECONDS * 1000, 0);
  refreshTimeoutId = setTimeout(() => {
    void ensureFreshAccess(refreshEndpoint).catch(() => {});
  }, delayMs);
}

async function doRefresh(refreshEndpoint = REFRESH_ENDPOINT) {
  const { refresh } = getTokens();
  if (!refresh) throw new Error('no_refresh_token');

  const resp = await fetch(refreshEndpoint, {
    method: 'POST',
    headers: { 
      'Content-Type': 'application/json', 
      'Accept': 'application/json'
    },
    body: JSON.stringify({ refresh })
  });
  
  if (!resp.ok) throw new Error('refresh_failed');

  const data = await resp.json();
  if (!data.access) throw new Error('no_access_in_response');

  setTokens({ access: data.access, refresh: data.refresh });
  return data.access;
}

async function ensureFreshAccess(refreshEndpoint = REFRESH_ENDPOINT) {
  if (!refreshPromise) {
    refreshPromise = doRefresh(refreshEndpoint)
      .catch((err) => {
        clearTokens();
        throw err;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

// Проверка авторизации
export function isAuthenticated() {
  const { access } = getTokens();
  return !!access && !isExpiring(access, 0);
}

// Получение информации о пользователе из токена
export function getUserInfo() {
  const { access } = getTokens();
  if (!access) return null;
  
  try {
    const payload = decodeJwt(access);
    return {
      username: payload.login || payload.username,
      role: payload.role,
      userId: payload.user_id || payload.profile_id,
      email_verified: payload.email_verified
    };
  } catch {
    return null;
  }
}

// Проверка роли администратора
export function isAdmin() {
  const user = getUserInfo();
  return user?.role === 'Администратор';
}

// Инициализация таймеров при запуске приложения
export function initAuthTimers(refreshEndpoint = REFRESH_ENDPOINT) {
  const { access } = getTokens();
  if (isExpiring(access)) {
    ensureFreshAccess(refreshEndpoint).catch(() => {});
  } else {
    scheduleProactiveRefresh(refreshEndpoint);
  }
  
  // Синхронизация между вкладками
  window.addEventListener('storage', (e) => {
    if (e.key === TOKEN_KEYS.ACCESS || e.key === TOKEN_KEYS.REFRESH) {
      scheduleProactiveRefresh(refreshEndpoint);
    }
  });
}

// Основная функция для запросов
export async function authFetch(url, options = {}, refreshEndpoint = REFRESH_ENDPOINT) {
  let { access } = getTokens();

  if (isExpiring(access)) {
    try {
      access = await ensureFreshAccess(refreshEndpoint);
    } catch {
      return { data: null, status: 401 };
    }
  }

  const headers = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    ...(access ? { 'Authorization': `Bearer ${access}` } : {}),
    ...(options.headers || {})
  };

  const fetchOptions = {
    ...options,
    headers
  };

  let response = await fetch(url, fetchOptions);

  if (response.status !== 401) {
    let data = null;
    try { data = await response.json(); } catch {}
    return { data, status: response.status };
  }

  // Попытка обновить токен
  try {
    const newAccess = await ensureFreshAccess(refreshEndpoint);
    const retryHeaders = { ...headers, 'Authorization': `Bearer ${newAccess}` };
    response = await fetch(url, { ...options, headers: retryHeaders });
  } catch {
    return { data: null, status: 401 };
  }

  let data = null;
  try { data = await response.json(); } catch {}
  return { data, status: response.status };
}

// Запрос к API OAuth
export async function oauthApi(endpoint, options = {}) {
  const baseUrl = 'https://oauth.eight-percent.com/api';
  const url = endpoint.startsWith('/') ? `${baseUrl}${endpoint}` : `${baseUrl}/${endpoint}`;
  
  return authFetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      ...options.headers
    }
  });
}

// Проверка сессии
export async function checkSession() {
  const { access } = getTokens();
  if (!access) return false;
  
  try {
    const response = await oauthApi('accounts/profile/check-token/');
    return response.status === 200;
  } catch {
    return false;
  }
}

// Извлечение токенов из URL (без отображения в адресной строке)
export function extractTokensFromURL() {
  // Проверяем только если это не OAuth домен (чтобы не удалять токены на oauth.eight-percent.com)
  if (window.location.hostname === 'oauth.eight-percent.com') {
    return false;
  }

  const url = new URL(window.location.href);
  const accessToken = url.searchParams.get('access_token');
  const refreshToken = url.searchParams.get('refresh_token');
  
  if (accessToken && refreshToken) {
    setTokens({ access: accessToken, refresh: refreshToken });
    
    // Удаляем токены из URL без перезагрузки
    url.searchParams.delete('access_token');
    url.searchParams.delete('refresh_token');
    window.history.replaceState({}, document.title, url.toString());
    
    return true;
  }
  
  return false;
}

// Функция для редиректа на OAuth с сохранением текущего URL
export function getOAuthRedirectUrl() {
  const currentUrl = window.location.href;
  return `https://oauth.eight-percent.com?next=${encodeURIComponent(currentUrl)}`;
}
